import { CssVarsThemeOptions } from "@mui/material/styles";
import "@mui/x-date-pickers/themeAugmentation";

type ThemeComponents = NonNullable<CssVarsThemeOptions["components"]>;

// Autofill is handled globally in App.css by clipping Chrome's highlight to the
// text. Masking it here with an inset box-shadow only covered the <input>, not
// the root's horizontal padding, which is what made these fields look two-tone.
export const inputTrasparentStyles = {
  "& .MuiInputBase-input": { backgroundColor: "transparent !important" },
  "& .MuiInputBase-root": { backgroundColor: "transparent !important" }
};

export const getInputDisabledStyles = (theme: any) => ({
  backgroundColor: theme.vars.palette.action.disabledBackground,
  borderColor: theme.vars.palette.border.subtle,
  color: theme.vars.palette.text.disabled,
  cursor: "not-allowed"
});

export const createInputOverrides = (): ThemeComponents => ({
  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        padding: "0 12px"
      },
      paper: ({ theme }) => ({
        "& .MuiAutocomplete-loading": {
          fontSize: theme.typography.body2.fontSize
        },
        "& .MuiAutocomplete-noOptions": {
          fontSize: theme.typography.body2.fontSize
        }
      })
    }
  },
  MuiFormHelperText: {
    styleOverrides: {
      // Helper text takes real layout height. It used to be pinned absolutely
      // below the FormControl so validation messages never shifted a form, but
      // that left it invisible to any ancestor's sizing — persistent, wrapping
      // helper text rendered outside its card's padding.
      root: ({ theme }) => ({
        fontSize: theme.typography.menuCaption.fontSize,
        margin: "4px 14px 0"
      })
    }
  },
  MuiInputBase: {
    styleOverrides: {
      multiline: {
        height: "auto"
      },
      root: ({ theme }) => ({
        "&.Mui-disabled": getInputDisabledStyles(theme),
        "&::placeholder": {
          fontSize: theme.typography.body2.fontSize,
          opacity: 0.4
        },
        backgroundColor: theme.vars.palette.background.default,
        borderRadius: theme.shape.borderRadiusButton,
        fontWeight: 500,
        height: 46,
        paddingLeft: 14
      })
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        // The `padding` shorthand below zeroes the horizontal padding MUI uses
        // to keep a Select's value clear of its overlaid chevron.
        "&.MuiSelect-select": {
          paddingRight: 32
        },
        "&:-webkit-autofill": {
          caretColor: theme.vars.palette.text.primary,
          WebkitBoxShadow: `0 0 0 100px transparent inset`,
          WebkitTextFillColor: theme.vars.palette.text.primary
        },
        padding: "13px 0"
      }),
      root: ({ theme }) => ({
        "&.Mui-disabled": {
          "& .MuiOutlinedInput-input": {
            backgroundColor: theme.vars.palette.action.disabledBackground,
            color: theme.vars.palette.text.disabled,
            cursor: "not-allowed",
            WebkitTextFillColor: theme.vars.palette.text.disabled
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.vars.palette.border.subtle
          },
          "& input:-webkit-autofill, & input:autofill": {
            caretColor: "transparent",
            WebkitTextFillColor: theme.vars.palette.text.disabled
          },
          backgroundColor: theme.vars.palette.action.disabledBackground
        },
        "&:not(.MuiSelect-root)": {
          padding: "0 14px"
        },
        backgroundColor: theme.vars.palette.background.default,
        borderRadius: theme.shape.borderRadiusButton,
        fontFamily: "Inter, sans-serif",
        fontSize: theme.typography.body2.fontSize
      })
    }
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-disabled": getInputDisabledStyles(theme),
        backgroundColor: theme.vars.palette.background.default,
        borderRadius: theme.shape.borderRadiusButton,
        fontWeight: 500,
        height: 46
      })
    }
  },

  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-disabled": {
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: theme.vars.palette.border.subtle
          },
          backgroundColor: theme.vars.palette.action.disabledBackground
        },
        backgroundColor: theme.vars.palette.background.default,
        borderRadius: theme.shape.borderRadiusButton,
        fontFamily: "Inter, sans-serif",
        fontSize: theme.typography.body2.fontSize
      })
    }
  },

  MuiPickersSectionList: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: "Inter, sans-serif",
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        padding: "13px 0"
      })
    }
  },

  MuiPickersTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& .Mui-disabled": getInputDisabledStyles(theme),
        width: "100%"
      })
    }
  },

  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& .Mui-disabled": getInputDisabledStyles(theme),
        width: "100%"
      })
    }
  }
});
